import React from 'react';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import DsSwitch from '../../common/DsSwitch';
import EventImageGallery from '../../common/events/EventImageGallery';
import EventMaterialExplorer from '../../common/events/EventMaterialExplorer';
import ChatTargetSearchModal from '../../common/chat/search/ChatTargetSearchModal';
import ChatSearchDatePresets from '../../common/chat/search/ChatSearchDatePresets';

export function EventChatSearchModal({
  isOpen,
  onClose,
  onApply,
  onClear,
  summary,
  applying,
  errorMessage,
  onRetry,
  retrying,
  searchDraft,
  setSearchDraft,
}) {
  return (
    <ChatTargetSearchModal
      isOpen={isOpen}
      title="イベントチャット検索"
      onClose={onClose}
      onApply={onApply}
      onClear={onClear}
      summary={summary}
      applying={applying}
      errorMessage={errorMessage}
      onRetry={onRetry}
      retrying={retrying}
    >
      <div className="chat-target-search-form">
        <div className="chat-target-search-form__field">
          <label className="chat-target-search-form__label" htmlFor="admin-event-search-query">
            イベント名 / 公演名
          </label>
          <input
            id="admin-event-search-query"
            className="chat-target-search-form__input"
            type="text"
            value={searchDraft.eventQuery}
            onChange={(event) =>
              setSearchDraft((current) => ({ ...current, eventQuery: event.target.value }))
            }
            placeholder="イベント名 / 公演名"
          />
        </div>

        <div className="chat-target-search-form__field">
          <label className="chat-target-search-form__label" htmlFor="admin-event-search-message">
            チャット本文
          </label>
          <input
            id="admin-event-search-message"
            className="chat-target-search-form__input"
            type="text"
            value={searchDraft.messageQuery}
            onChange={(event) =>
              setSearchDraft((current) => ({ ...current, messageQuery: event.target.value }))
            }
            placeholder="本文キーワード"
          />
        </div>

        <div className="chat-target-search-form__field">
          <span className="chat-target-search-form__label">公演日クイック指定</span>
          <ChatSearchDatePresets
            startDate={searchDraft.startDate}
            endDate={searchDraft.endDate}
            onSelect={({ startDate, endDate }) =>
              setSearchDraft((current) => ({ ...current, startDate, endDate }))
            }
          />
        </div>

        <div className="chat-target-search-form__field">
          <label className="chat-target-search-form__label" htmlFor="admin-event-search-start-date">
            公演日（開始）
          </label>
          <input
            id="admin-event-search-start-date"
            className="chat-target-search-form__input"
            type="date"
            value={searchDraft.startDate}
            onChange={(event) =>
              setSearchDraft((current) => ({ ...current, startDate: event.target.value }))
            }
          />
        </div>

        <div className="chat-target-search-form__field">
          <label className="chat-target-search-form__label" htmlFor="admin-event-search-end-date">
            公演日（終了）
          </label>
          <input
            id="admin-event-search-end-date"
            className="chat-target-search-form__input"
            type="date"
            value={searchDraft.endDate}
            onChange={(event) =>
              setSearchDraft((current) => ({ ...current, endDate: event.target.value }))
            }
          />
        </div>
      </div>
    </ChatTargetSearchModal>
  );
}

export function EventMentionSilenceConfirmModal({
  confirm,
  onClose,
}) {
  if (!confirm) return null;

  return (
    <DsModal
      open={Boolean(confirm)}
      onClose={() => onClose(false)}
      title={(
        <>
          <i className="fa-solid fa-bell" aria-hidden="true"></i>
          <span>メンション通知は通常通知で届きます</span>
        </>
      )}
      size="sm"
      className="event-chat-mention-silence-confirm__dialog"
      footer={(
        <>
          <DsButton type="button" variant="secondary" onClick={() => onClose(false)}>
            キャンセル
          </DsButton>
          <DsButton type="button" onClick={() => onClose(true)}>
            送信する
          </DsButton>
        </>
      )}
    >
      <div className="settings-modal__body">
        <p className="settings-modal__subtitle">
          {confirm.eventName} は現在、通知サイレント時間中です。イベントチャットのメンション通知だけは音・バイブありの通常通知として送信されます。
        </p>
        <p className="settings-hint">
          本番中などにスマホを鳴らしたくない場合は、メンションせずに送信してください。
        </p>
      </div>
    </DsModal>
  );
}

export function EventMaterialsModal({
  isOpen,
  eventId,
  canView,
  canUpload,
  canManage,
  canDownload = false,
  canViewAllLogs,
  onClose,
}) {
  if (!isOpen || !eventId) return null;

  return (
    <DsModal
      open={isOpen}
      onClose={onClose}
      title="イベント資料"
      size="lg"
      className="event-material-chat-modal"
    >
      <EventMaterialExplorer
        eventUuid={eventId}
        role="admin"
        title="資料"
        canView={canView}
        canUpload={canUpload}
        canManage={canManage}
        canDownload={canDownload}
        canViewAllLogs={canViewAllLogs}
        showTitle={false}
        emptyLabel="登録された資料はありません"
      />
    </DsModal>
  );
}

export function EventAlbumModal({
  isOpen,
  eventId,
  eventName,
  eventDateLabel,
  canView,
  canUpload,
  canDownload,
  onClose,
}) {
  if (!isOpen || !eventId) return null;

  return (
    <DsModal
      open={isOpen}
      onClose={onClose}
      title="イベントアルバム"
      size="lg"
      className="event-material-chat-modal event-album-chat-modal"
    >
      <EventImageGallery
        eventUuid={eventId}
        eventName={eventName}
        eventDateLabel={eventDateLabel}
        canView={canView}
        canUpload={canUpload}
        canDownload={canDownload}
        showTitle={false}
      />
    </DsModal>
  );
}

export function EventChatSettingsModal({
  isOpen,
  onClose,
  settingsEvent,
  selectedEventInfo,
  isSettingsGroupUnified,
  isLoadingSettings,
  isSavingSettings,
  muteSetting,
  onMuteToggle,
}) {
  if (!isOpen) return null;

  const performanceType = settingsEvent?.performance_type || selectedEventInfo?.performance_type;

  return (
    <DsModal
      open={isOpen}
      onClose={onClose}
      title={(
        <>
          <i className="fa-solid fa-gear" aria-hidden="true"></i>
          <span>通知設定</span>
        </>
      )}
      size="sm"
      className="event-chat-settings-modal settings-modal"
    >
      <p className="settings-modal__subtitle">
        {settingsEvent?.name || selectedEventInfo?.name || 'イベント名未設定'}
        {!isSettingsGroupUnified && performanceType ? ` / ${performanceType}` : ''}
      </p>
      <div className="settings-modal__body">
        {isLoadingSettings ? (
          <div className="settings-modal__loading">読み込み中です…</div>
        ) : (
          <>
            <DsSwitch
              checked={!muteSetting}
              onChange={(next) => onMuteToggle(!next)}
              disabled={isSavingSettings}
              label="通知オン"
              ariaLabel="このイベントの通知オンオフ"
            />
            <div className="settings-inline-status">
              {isSavingSettings ? '更新中…' : (muteSetting ? '通知オフ' : '通知オン')}
            </div>
            <div className="settings-hint">メール/LINE/プッシュ通知に影響します。</div>
          </>
        )}
      </div>
    </DsModal>
  );
}
